import React, {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {ErrorsType} from 'reactjs-use-form'

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {
  Box,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Tooltip,
  tooltipClasses,
} from '@mui/material'
import {PGTypography} from '@progynyglobal/pg-ui-library'

import {
  BankFieldSpecType,
  useGetBankFieldsSchemaQuery,
} from '@progyny/generated/graphql'
import {PaymentFormValues} from '@progyny/pages/Budget/Reimbursement/helpers/PaymentFormModel'
import {
  parseLocalFields,
  setLocalFieldValue,
} from '@progyny/pages/Budget/Reimbursement/helpers/util'

import {theme} from '@root/theme'

type CountryBankCodeFieldsProps = {
  values: Partial<PaymentFormValues>
  errors: ErrorsType
  handleOnChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}

type CodeInput = {kind: 'swift'} | {kind: 'schema'; spec: BankFieldSpecType}

const SWIFT_LABEL_KEY = 'profile_bank_details_swift_bic'
const SWIFT_PLACEHOLDER_KEY = 'profile_bank_details_enter_swift'
const SWIFT_TOOLTIP_KEY = 'profile_bank_details_swift_tooltip'

const CountryBankCodeFields: React.FC<CountryBankCodeFieldsProps> = ({
  values,
  errors,
  handleOnChange,
}) => {
  const {t} = useTranslation()
  const country = values?.country ?? ''

  const {data, loading} = useGetBankFieldsSchemaQuery({
    variables: {countryCode: country},
    skip: !country,
    fetchPolicy: 'cache-first',
  })

  const schema = data?.bankFieldsSchema
  const schemaFields = useMemo(() => schema?.fields ?? [], [schema?.fields])
  const swiftRequired = schema?.swiftRequired ?? true
  // When the schema returns no country-specific fields we fall back to showing
  // SWIFT regardless of `swiftRequired` so users always have a code field.
  const showSwift = schemaFields.length === 0 || swiftRequired

  const codeInputs = useMemo<CodeInput[]>(() => {
    const inputs: CodeInput[] = []
    if (showSwift) inputs.push({kind: 'swift'})
    schemaFields.forEach((spec) => inputs.push({kind: 'schema', spec}))
    return inputs
  }, [showSwift, schemaFields])

  const localFieldValues = useMemo(
    () => parseLocalFields(values?.localFields),
    [values?.localFields]
  )

  const [touchedLocalFields, setTouchedLocalFields] = useState<
    Record<string, boolean>
  >({})

  const handleLocalFieldChange = (fieldName: string, fieldValue: string) => {
    const nextJson = setLocalFieldValue(
      values?.localFields,
      fieldName,
      fieldValue
    )
    setTouchedLocalFields((prev) => ({...prev, [fieldName]: true}))
    handleOnChange({
      target: {name: 'localFields', value: nextJson},
    } as React.ChangeEvent<HTMLInputElement>)
  }

  const handleSwiftChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTouchedLocalFields((prev) => ({...prev, swiftCode: true}))
    handleOnChange(e)
  }

  const renderCodeInput = (input: CodeInput, key: string) => {
    if (input.kind === 'swift') {
      const swiftRequiredNow = swiftRequired || schemaFields.length === 0
      const swiftHasError =
        swiftRequiredNow && touchedLocalFields.swiftCode && !values?.swiftCode
      return (
        <CodeFieldWithTooltip
          key={key}
          name='swiftCode'
          label={t(SWIFT_LABEL_KEY)}
          placeholder={t(SWIFT_PLACEHOLDER_KEY)}
          tooltipBody={t(SWIFT_TOOLTIP_KEY)}
          required={swiftRequiredNow}
          value={values?.swiftCode ?? ''}
          error={swiftHasError}
          helperText={swiftHasError ? t('common_this_field_is_required') : ''}
          onChange={handleSwiftChange}
          whereToFindLabel={t('profile_bank_details_where_to_find_it')}
        />
      )
    }
    const {spec} = input
    const specHasError =
      spec.required &&
      touchedLocalFields[spec.name] &&
      !localFieldValues[spec.name]?.trim()
    return (
      <CodeFieldWithTooltip
        key={key}
        name={spec.name}
        label={spec.label}
        placeholder=''
        tooltipBody={spec.helpText ?? ''}
        required={spec.required}
        value={localFieldValues[spec.name] ?? ''}
        onChange={(e) => handleLocalFieldChange(spec.name, e.target.value)}
        whereToFindLabel={t('profile_bank_details_where_to_find_it')}
        error={specHasError}
        helperText={specHasError ? t('common_this_field_is_required') : ''}
      />
    )
  }

  if (loading) {
    return (
      <Box
        display='flex'
        mt={2}
        flexDirection={{xs: 'column', md: 'row'}}
        gap={2}
      >
        <Box
          width='100%'
          display='flex'
          alignItems='center'
          justifyContent='flex-start'
          py={2}
        >
          <CircularProgress data-testid='bank-fields-loading' size={24} />
        </Box>
        <BankNameField
          values={values}
          errors={errors}
          handleOnChange={handleOnChange}
        />
      </Box>
    )
  }

  const [primary, ...rest] = codeInputs

  return (
    <>
      <Box
        display='flex'
        mt={2}
        flexDirection={{xs: 'column', md: 'row'}}
        gap={2}
      >
        {primary ? renderCodeInput(primary, 'primary') : <Box width='100%' />}
        <BankNameField
          values={values}
          errors={errors}
          handleOnChange={handleOnChange}
        />
      </Box>

      {rest.map((input, index) => (
        <Box
          key={`code-row-${index}`}
          display='flex'
          mt={2}
          flexDirection={{xs: 'column', md: 'row'}}
          gap={2}
        >
          {renderCodeInput(input, `secondary-${index}`)}
          <Box width='100%' />
        </Box>
      ))}
    </>
  )
}

export default CountryBankCodeFields

type CodeFieldWithTooltipProps = {
  name: string
  label: string
  placeholder: string
  tooltipBody: string
  required: boolean
  value: string
  error?: boolean
  helperText?: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  whereToFindLabel: string
}

const CodeFieldWithTooltip: React.FC<CodeFieldWithTooltipProps> = ({
  name,
  label,
  placeholder,
  tooltipBody,
  required,
  value,
  error,
  helperText,
  onChange,
  whereToFindLabel,
}) => (
  <Stack width='100%'>
    <TextField
      required={required}
      label={label}
      fullWidth
      multiline
      variant='outlined'
      margin='dense'
      placeholder={placeholder}
      value={value}
      name={name}
      onChange={onChange}
      error={error}
      helperText={helperText}
      inputProps={{'data-testid': `input-${name}`}}
    />
    {tooltipBody ? (
      <Tooltip
        arrow
        sx={{width: 200}}
        title={tooltipBody}
        slotProps={{popper: {sx: tooltipPopperSx}}}
      >
        <Grid
          pt={1}
          width={'fit-content'}
          display={'flex'}
          alignItems={'center'}
          gap={'4px'}
          flexDirection={'row'}
        >
          <InfoOutlinedIcon
            sx={{color: theme.palette.other.brandColors.lightGray}}
          />
          <PGTypography
            variant='label'
            sx={{
              fontSize: 12,
              color: theme.palette.other.brandColors.lightGray,
            }}
          >
            {whereToFindLabel}
          </PGTypography>
        </Grid>
      </Tooltip>
    ) : null}
  </Stack>
)

type BankNameFieldProps = Pick<
  CountryBankCodeFieldsProps,
  'values' | 'errors' | 'handleOnChange'
>

const BankNameField: React.FC<BankNameFieldProps> = ({
  values,
  errors,
  handleOnChange,
}) => {
  const {t} = useTranslation()
  return (
    <TextField
      required
      label={t('profile_bank_details_bank_name')}
      fullWidth
      multiline
      variant='outlined'
      margin='dense'
      placeholder={t('profile_bank_details_enter_bank_name')}
      value={values?.bankName ?? ''}
      name='bankName'
      onChange={handleOnChange}
      error={errors?.bankName?.hasError}
      helperText={t(errors?.bankName?.message ?? '')}
    />
  )
}

const tooltipPopperSx = {
  [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
    {
      marginTop: '0px',
    },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'black',
    color: 'white',
    fontSize: 12,
    lineHeight: 1.4,
    padding: '16px 24px',
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: 'black',
  },
  width: 230,
}
